import React, { useRef, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { motion } from "framer-motion";
import Sticky from "react-sticky-el";
import { arrayOf, node, oneOfType, string, number } from "prop-types";
import { useMedia } from "react-use";

import ContentWrapper from "../../../shared/ContentWrapper";
import { useMap } from "@shared/v2/widgets/useMap";

import { color, tabletLarge } from "@components/shared/utils";
import useTestDriveMapHeightCalculator from "@hooks/useTestDriveMapHeightCalculator";

const TestDriveWrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  background: yellow;
  color: ${color("black")};
  ${tabletLarge`
    min-height: calc(100vh - 6rem)
  `}
`;

const StyledContentWrapper = styled(ContentWrapper)`
  ${tabletLarge`
    padding: 0 1rem;
  `}
`;

const MapLocationContainer = styled.div`
  display: none;

  ${tabletLarge`
      display: block;
      flex-basis: 60%;
      background: ${color("white")};
      min-height: calc(100vh - 6rem)
    `}
`;

const MapStickyContainer = styled.div`
  height: 100vh;
`;

const ProgressContainer = styled.div`
  border-bottom: 1px solid ${color("black")};
  background-color: ${color("white")};
  padding-top: 2rem;
  flex-basis: 100%;
  overflow-y: auto;

  section {
    display: flex;
    padding: 0 1rem;
  }
  ${tabletLarge`
    flex-basis: 46%;
    max-width: 46%;
    overflow-x: hidden;
  `}
`;

const TestDriveProgressLayout = ({
  geoLatitude,
  geoLongitude,
  markerImageUrl,
  children,
}) => {
  const theme = useTheme();
  const isTabletMedia = useMedia(`(min-width: ${theme.breakPoints.tablet})`);
  const { calculatedMapHeight } = useTestDriveMapHeightCalculator({
    shouldUpdate: isTabletMedia,
  });

  const mapContainerRef = useRef(null);

  const map = useMap({
    containerRef: mapContainerRef,
    options: {
      zoom: 12,
      center: { lat: Number(geoLatitude), lng: Number(geoLongitude) },
      options: {
        disableDefaultUI: false,
      },
    },
  });

  useEffect(() => {
    if (!map) {
      return;
    }

    const mapMarker = map.createMarker({
      position: { lat: Number(geoLatitude), lng: Number(geoLongitude) },
      icon: {
        url: markerImageUrl,
        scaledSize: new map.maps.Size(35, 35),
      },
    });

    map.render(mapMarker);

    return () => {
      map.remove(mapMarker);
    };
  }, [map, geoLatitude, geoLongitude, markerImageUrl]);

  return (
    <StyledContentWrapper isWholeWidth color={theme.colors.white}>
      <TestDriveWrapper>
        <MapLocationContainer data-id="boundary">
          <Sticky
            hideOnBoundaryHit={false}
            boundaryElement="[data-id=boundary]"
            positionRecheckInterval={10}
          >
            <MapStickyContainer>
              <motion.div
                style={{ height: calculatedMapHeight }}
                ref={mapContainerRef}
              />
            </MapStickyContainer>
          </Sticky>
        </MapLocationContainer>
        <ProgressContainer>{children}</ProgressContainer>
      </TestDriveWrapper>
    </StyledContentWrapper>
  );
};

TestDriveProgressLayout.propTypes = {
  geoLatitude: oneOfType([string, number]).isRequired,
  geoLongitude: oneOfType([string, number]).isRequired,
  children: oneOfType([node, arrayOf(node)]),
  markerImageUrl: string,
};

export default TestDriveProgressLayout;
